/**
 * `ActiveFilter` — строка «Показано: … · Сбросить» над данными, которые
 * сужены выбором на экране (agroai-6al.37). Правило: любой фильтр экрана
 * объекта виден словами и снимается одной кнопкой — пользователь не должен
 * гадать, почему таблица короче, чем вчера.
 *
 *   <ActiveFilter items={[{ key: 'cycle', text: 'цикл Хлопок 2026', onClear }]} onReset={reset} />
 *
 * Ни одного снимаемого фильтра нет — строка показывает `idle` (что видно по
 * умолчанию), без кнопки сброса; `idle` не задан — строки нет.
 */

import { Filter, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { Icon } from './Icon';
import './active-filter.css';

export interface ActiveFilterItem {
  key: string;
  /** Что выбрано, словами: «цикл Хлопок 2026», «статус: после срока». */
  text: ReactNode;
  /** Снять только этот фильтр; без него чип не снимается отдельно. */
  onClear?: () => void;
}

export interface ActiveFilterProps {
  items: ActiveFilterItem[];
  /** Снять все фильтры экрана (вернуть умолчание). */
  onReset?: () => void;
  /** Подпись строки; по умолчанию «Показано». */
  label?: string;
  /** Что видно без фильтров — строка вместо чипов, когда фильтров нет. */
  idle?: ReactNode;
  resetLabel?: string;
}

const m = defineMessages({
  label: {
    id: 'common.ui.activeFilter.label',
    defaultMessage: 'Показано',
    description: 'Подпись строки активных фильтров над таблицей или списком («Показано: цикл Хлопок 2026»); после неё ставится двоеточие',
  },
  reset: {
    id: 'common.ui.activeFilter.reset',
    defaultMessage: 'Сбросить',
    description: 'Кнопка в строке активных фильтров: снять все фильтры экрана; коротко',
  },
  clearOne: {
    id: 'common.ui.activeFilter.clearOne',
    defaultMessage: 'Снять этот фильтр',
    description: 'aria-label крестика на чипе фильтра: снять только этот фильтр',
  },
});

export function ActiveFilter({ items, onReset, label, idle, resetLabel }: ActiveFilterProps) {
  const t = useT();
  if (items.length === 0 && !idle) return null;
  return (
    <div className="afilter" role="status" aria-live="polite">
      <Icon icon={Filter} size={16} />
      <span className="afilter__label">{label ?? t(m.label)}:</span>
      {items.length === 0 ? (
        <span className="afilter__idle">{idle}</span>
      ) : (
        <span className="afilter__chips">
          {items.map((item) => (
            <span key={item.key} className="afilter__chip">
              <span>{item.text}</span>
              {item.onClear ? (
                <button type="button" className="afilter__x focus-ring" aria-label={t(m.clearOne)} onClick={item.onClear}>
                  <Icon icon={X} size={16} />
                </button>
              ) : null}
            </span>
          ))}
        </span>
      )}
      {items.length > 0 && onReset ? (
        <button type="button" className="afilter__reset focus-ring" onClick={onReset}>
          <Icon icon={X} size={16} /> {resetLabel ?? t(m.reset)}
        </button>
      ) : null}
    </div>
  );
}
